import { useNavigate, useSearchParams } from "react-router-dom";
import { useGetSearchQuery } from "../../apiCaller/apis/getApi";
import ClientLayout from "../../components/Layout/ClientLayout";
import EventComponent from "../../components/eventCard/EventComponent";
import ProgrammeComponent from "../../components/eventCard/ProgrammeComponent";
import SpinningLoader from "../../components/SpinningLoader/SpinningLoader";
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Search as SearchIcon, Mountain, Sparkles, TrendingUp, ArrowRight } from "lucide-react";

const SearchResults = () => {
  const [searchParams] = useSearchParams();
  const searchText = searchParams.get("text") || "";
  const [searchQuery, setSearchQuery] = useState(searchText);
  const navigate = useNavigate();

  useEffect(() => {
    setSearchQuery(searchText);
  }, [searchText]);

  const {
    data: searchResults,
    error,
    isLoading,
    isFetching,
  } = useGetSearchQuery({ text: searchText });

  const handleAdventureClick = (id) => {
    navigate(`/events/${id}`);
  };

  const handleProgrammeClick = (id) => {
    navigate(`/programmes/${id}`);
  };

  const totalResults =
    (searchResults?.adventures?.length || 0) +
    (searchResults?.programmes?.length || 0);

  return (
    <ClientLayout>
      {/* Hero Section */}
      <section className="relative h-[500px] lg:h-[600px] overflow-hidden">
        {/* Background Image */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-1551632811-561732d1e306?w=1920&q=90)`,
          }}
        />

        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-br from-mamba-navy/95 via-slate-900/85 to-mamba-navy/90" />
        <div className="absolute inset-0 bg-gradient-to-t from-mamba-navy/95 via-transparent to-transparent" />

        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 10, 0],
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -top-40 -right-40 w-96 h-96 bg-mamba-sky-blue/20 rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              scale: [1, 1.3, 1],
              rotate: [0, -10, 0],
              opacity: [0.2, 0.4, 0.2],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -bottom-40 -left-40 w-96 h-96 bg-mamba-golden/20 rounded-full blur-3xl"
          />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-5">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-mamba-golden/20 backdrop-blur-sm rounded-full border border-mamba-golden/40 mb-6"
          >
            <SearchIcon className="w-4 h-4 text-mamba-golden" />
            <span className="text-sm font-bold font-montserrat text-mamba-golden uppercase tracking-wider">
              Search Results
            </span>
          </motion.div>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-5xl md:text-6xl lg:text-7xl font-black font-montserrat text-white mb-4 leading-tight"
          >
            Find Your <span className="italic font-dancing-script text-mamba-golden">Adventure</span>
          </motion.h1>

          {searchText && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl md:text-2xl text-white/90 font-inter mb-8"
            >
              Searching for: <span className="font-bold text-mamba-golden">"{searchText}"</span>
            </motion.div>
          )}

          {/* Results Count */}
          {!isLoading && totalResults > 0 && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="inline-flex items-center gap-3 px-6 py-3 bg-white/10 backdrop-blur-md rounded-full border border-white/20"
            >
              <TrendingUp className="w-5 h-5 text-mamba-golden" />
              <span className="text-white font-montserrat font-semibold">
                {totalResults} {totalResults === 1 ? 'Result' : 'Results'} Found
              </span>
            </motion.div>
          )}
        </div>
      </section>

      {/* Search Input Section */}
      <section className="px-5 md:px-10 lg:px-20 xl:px-32 py-12 bg-gradient-to-br from-white via-mamba-bg-light to-white">
        <div className="max-w-4xl mx-auto">
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            onSubmit={(e) => {
              e.preventDefault();
              if (searchQuery.trim()) {
                navigate(`/search?text=${encodeURIComponent(searchQuery.trim())}`);
              }
            }}
            className="relative group"
          >
            <input
              type="text"
              placeholder="Search destinations, events, programmes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-6 py-4 pr-14 rounded-2xl border-2 border-mamba-navy/20 focus:border-mamba-sky-blue focus:outline-none transition-all placeholder:text-gray-400 font-inter text-lg shadow-lg"
            />
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="submit"
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-gradient-to-r from-mamba-sky-blue to-mamba-navy text-white p-3 rounded-xl hover:shadow-lg transition-all"
            >
              <SearchIcon className="w-5 h-5" />
            </motion.button>
          </motion.form>
        </div>
      </section>

      {/* SEARCH RESULTS */}
      <section className="px-5 md:px-10 lg:px-20 xl:px-32 py-16 bg-white">
        <div className="max-w-8xl mx-auto">
          {isLoading || isFetching ? (
            <div className="flex items-center justify-center py-20">
              <SpinningLoader isLoading={true} hasData={false} />
            </div>
          ) : (
            <>
              {totalResults === 0 ? (
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="w-full text-center py-20"
                >
                  <div className="inline-flex items-center justify-center w-24 h-24 bg-mamba-bg-light rounded-full mb-6">
                    <Mountain className="w-12 h-12 text-mamba-navy" />
                  </div>
                  <h3 className="text-3xl font-black font-montserrat text-mamba-navy mb-4">
                    No Results Found
                  </h3>
                  <p className="text-lg font-inter text-slate-600 mb-8 max-w-2xl mx-auto">
                    We couldn't find any matches for your search. Try adjusting your search terms or explore our adventures below.
                  </p>
                  <div className="flex flex-wrap gap-4 justify-center">
                    <motion.button
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => navigate("/events")}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-mamba-sky-blue to-mamba-navy text-white font-bold font-montserrat rounded-full shadow-lg hover:shadow-xl transition-all"
                    >
                      Browse Events
                      <ArrowRight className="w-5 h-5" />
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => navigate("/programmes")}
                      className="inline-flex items-center gap-2 px-6 py-3 bg-white border-2 border-mamba-navy text-mamba-navy font-bold font-montserrat rounded-full shadow-lg hover:shadow-xl hover:bg-mamba-bg-light transition-all"
                    >
                      View Programmes
                      <ArrowRight className="w-5 h-5" />
                    </motion.button>
                  </div>
                </motion.div>
              ) : (
                <>
                  {/* Adventures/Events Section */}
                  {searchResults?.adventures &&
                    searchResults.adventures.length > 0 && (
                      <motion.section
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="mb-16"
                      >
                        {/* Section Header */}
                        <div className="flex items-center gap-4 mb-8">
                          <div className="flex-shrink-0">
                            <div className="h-1 w-12 bg-gradient-to-r from-mamba-sky-blue to-transparent rounded-full" />
                          </div>
                          <h2 className="text-3xl md:text-4xl font-black font-montserrat text-mamba-navy">
                            Events
                            <span className="ml-3 inline-flex items-center justify-center w-10 h-10 bg-mamba-sky-blue/20 text-mamba-sky-blue rounded-full text-lg font-bold">
                              {searchResults.adventures.length}
                            </span>
                          </h2>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
                          {searchResults.adventures.map((adventure, index) => (
                            <motion.div
                              key={adventure.id}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.4, delay: index * 0.1 }}
                              onClick={() => handleAdventureClick(adventure.slug)}
                              className="cursor-pointer"
                            >
                              <EventComponent adventure={adventure} />
                            </motion.div>
                          ))}
                        </div>
                      </motion.section>
                    )}

                  {/* Programmes Section */}
                  {searchResults?.programmes &&
                    searchResults.programmes.length > 0 && (
                      <motion.section
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="mb-16"
                      >
                        {/* Section Header */}
                        <div className="flex items-center gap-4 mb-8">
                          <div className="flex-shrink-0">
                            <div className="h-1 w-12 bg-gradient-to-r from-mamba-golden to-transparent rounded-full" />
                          </div>
                          <h2 className="text-3xl md:text-4xl font-black font-montserrat text-mamba-navy">
                            Programmes
                            <span className="ml-3 inline-flex items-center justify-center w-10 h-10 bg-mamba-golden/20 text-mamba-golden rounded-full text-lg font-bold">
                              {searchResults.programmes.length}
                            </span>
                          </h2>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
                          {searchResults.programmes.map((programme, index) => (
                            <motion.div
                              key={programme.id}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ duration: 0.4, delay: index * 0.1 }}
                              onClick={() => handleProgrammeClick(programme.slug)}
                              className="cursor-pointer"
                            >
                              <ProgrammeComponent program={programme} />
                            </motion.div>
                          ))}
                        </div>
                      </motion.section>
                    )}
                </>
              )}
            </>
          )}
        </div>
      </section>
    </ClientLayout>
  );
};

export default SearchResults;
